import { type CSSProperties, type ReactNode, useRef } from "react";
import countriesData from "../../../worker/games/worldle/countries.json";
import { useWorldShapes } from "../geo/useWorldShapes";
import { type Box, cameraBox, fitTransform } from "../puzzle/route";
import "./neighbours.css";

interface CountryRow {
  code: string;
  uk?: string;
  name: string;
  lat: number;
  lng: number;
  borders?: string[];
}
const COUNTRIES = new Map((countriesData as CountryRow[]).map((c) => [c.code, c]));

const VW = 360;
const VH = 270;

interface NeighbourMapProps {
  host: { code: string; name: string };
  /** Neighbour codes named so far. */
  found: string[];
  /** First letters of still-missing neighbours, in the server's (borders) order. */
  hints: string[];
  status: "playing" | "won" | "lost";
}

/**
 * Сусіди's map: only the day's country and its land neighbours are drawn — no
 * world around them. Neighbours are dashed outlines until named, then fill in with
 * their name; tiny ones missing from the 110m map are a dot at their coordinates.
 * Hint letters sit inside the outline they belong to (same order the server uses).
 */
export function NeighbourMap({ host, found, hints, status }: NeighbourMapProps) {
  const shapes = useWorldShapes();
  const restored = useRef<Set<string> | null>(null);
  restored.current ??= new Set(found);

  const neighbours = COUNTRIES.get(host.code)?.borders ?? [];
  const missing = neighbours.filter((n) => !found.includes(n));
  const hintFor = new Map(missing.slice(0, hints.length).map((code, i) => [code, hints[i] ?? ""]));

  const hostShape = shapes.by.get(host.code);
  const hostRow = COUNTRIES.get(host.code);
  const hostPoint = hostShape?.c ?? (hostRow ? shapes.project(hostRow.lng, hostRow.lat) : null);
  const hostBox = hostShape?.box ?? (hostPoint ? ([hostPoint[0] - 4, hostPoint[1] - 4, hostPoint[0] + 4, hostPoint[1] + 4] as const) : null);

  // Frame the neighbourhood: the country plus each neighbour's main body, but a
  // neighbour only counts within reach of the country (its box clipped to the
  // country's box grown by 1.1× its size) — Russia or Borneo can't zoom the map out.
  const box = (() => {
    if (!hostBox) return null;
    const [hx0, hy0, hx1, hy1] = hostBox;
    const reach = Math.max(hx1 - hx0, hy1 - hy0, 8) * 1.1;
    const boxes: Box[] = [[hx0, hy0, hx1, hy1]];
    for (const code of neighbours) {
      const nb = shapes.by.get(code)?.box;
      const row = COUNTRIES.get(code);
      const p = nb ? null : row ? shapes.project(row.lng, row.lat) : null;
      const [nx0, ny0, nx1, ny1] = nb ?? (p ? [p[0] - 2, p[1] - 2, p[0] + 2, p[1] + 2] : [hx0, hy0, hx1, hy1]);
      const cx0 = Math.max(nx0, hx0 - reach);
      const cy0 = Math.max(ny0, hy0 - reach);
      const cx1 = Math.min(nx1, hx1 + reach);
      const cy1 = Math.min(ny1, hy1 + reach);
      if (cx0 < cx1 && cy0 < cy1) boxes.push([cx0, cy0, cx1, cy1]);
    }
    return cameraBox(boxes, 0.08, 36);
  })();
  const cam = box ? fitTransform(box, VW, VH) : { k: 1, x: 0, y: 0 };
  const k = cam.k;

  /** World point → a label position kept inside the frame. */
  const inside = (p: [number, number], margin = 26): [number, number] => {
    const vx = Math.min(VW - margin, Math.max(margin, p[0] * k + cam.x));
    const vy = Math.min(VH - 14, Math.max(14, p[1] * k + cam.y));
    return [(vx - cam.x) / k, (vy - cam.y) / k];
  };

  const label = (p: [number, number], text: string, className: string, size = 11): ReactNode => {
    const [x, y] = inside(p);
    return (
      <text className={`nm-label ${className}`} x={x} y={y} fontSize={size / k} strokeWidth={3 / k}>
        {text}
      </text>
    );
  };

  const shapeOf = (code: string) => {
    const s = shapes.by.get(code);
    if (s) {
      // Label the part of a neighbour that faces the country (Malaysia's peninsula, not Borneo).
      const ref = hostPoint ?? s.c;
      const at = s.parts.reduce((best, p) => (Math.hypot(p[0] - ref[0], p[1] - ref[1]) < Math.hypot(best[0] - ref[0], best[1] - ref[1]) ? p : best), s.c);
      return { node: <path d={s.d} vectorEffect="non-scaling-stroke" />, at: code === host.code ? s.c : at };
    }
    const row = COUNTRIES.get(code);
    const p = row ? shapes.project(row.lng, row.lat) : null;
    return p ? { node: <circle cx={p[0]} cy={p[1]} r={6 / k} vectorEffect="non-scaling-stroke" />, at: p as [number, number] } : null;
  };

  // Order round the country for the win wave.
  const around = [...neighbours].sort((a, b) => angleOf(a) - angleOf(b));
  function angleOf(code: string): number {
    const at = shapeOf(code)?.at;
    if (!at || !hostPoint) return 0;
    return Math.atan2(at[0] - hostPoint[0], hostPoint[1] - at[1]);
  }

  return (
    <div className={`nm ${status === "won" ? "nm-won" : ""}`}>
      <svg className="nm-board" viewBox={`0 0 ${VW} ${VH}`} role="img" aria-label={`${host.name} і сусіди`}>
        <g transform={`translate(${cam.x} ${cam.y}) scale(${k})`} style={{ "--k": k } as CSSProperties}>
          <path className="nm-grid" d={shapes.graticule} vectorEffect="non-scaling-stroke" />
          {neighbours.map((code) => {
            const shape = shapeOf(code);
            if (!shape) return null;
            const isFound = found.includes(code);
            const state = isFound ? "nm-found" : status === "lost" ? "nm-missed" : "";
            const fresh = isFound && !restored.current?.has(code);
            return (
              <g key={code} className={`nm-n ${state} ${fresh ? "nm-fresh" : ""}`} style={{ "--i": around.indexOf(code) } as CSSProperties}>
                {shape.node}
              </g>
            );
          })}
          {(() => {
            const shape = shapeOf(host.code);
            return shape ? <g className="nm-host">{shape.node}</g> : null;
          })()}
          {neighbours.map((code) => {
            const shape = shapeOf(code);
            if (!shape) return null;
            const isFound = found.includes(code);
            const name = COUNTRIES.get(code)?.uk ?? code;
            if (isFound || status === "lost") {
              return (
                <g key={`l:${code}:${isFound}`} className={isFound && !restored.current?.has(code) ? "nm-pop" : ""}>
                  {label(shape.at, name, "")}
                </g>
              );
            }
            const hint = hintFor.get(code);
            return <g key={`l:${code}`}>{label(shape.at, hint ? `${hint}…` : "?", "nm-label-quiet", hint ? 12 : 13)}</g>;
          })}
          {hostPoint && label(hostPoint, host.name, "nm-label-host", 12)}
        </g>
      </svg>
    </div>
  );
}
